import { PostEditBaseCtrl } from 'components/sharpr/post/service/PostEditBaseCtrl';
import { ensure } from 'components/sharpr/ensure/ensure';
import spinner from 'components/sharpr/spinner/SpinnerSingleton';
import moment from 'moment';
import { app as msTeamsApp, dialog as msTeamsDialog } from '@microsoft/teams-js';
import _ from 'lodash';

export function PostCreateFromExternalRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.PostCreateFromExternal', {
		url: '/post/incoming',
		controllerAs: 'vm',
		controller: PostCreateFromExternalCtrl,
		templateUrl: '/ng/hub/Post/Edit/PostEditCtrl.html?' + window.BUILD,
		resolve: {
			$title: () => 'Post to Sharpr',
		},
	});
}

export class PostCreateFromExternalCtrl extends PostEditBaseCtrl {
	constructor(
		Identity,
		Api,
		Theme,
		Cfg,
		AutoArchive,
		FileDrag,
		$q,
		$timeout,
		$interval,
		$http,
		modal,
		$location,
		$scope,
		ClientChooserWhitelist
	) {
		'ngInject';
		super();

		this.Identity = Identity;
		this.Theme = Theme;
		this.Api = Api;
		this.Cfg = Cfg;
		this.AutoArchive = AutoArchive;
		this.FileDrag = FileDrag;
		this.$q = $q;
		this.$timeout = $timeout;
		this.$interval = $interval;
		this.$http = $http;
		this.modal = modal;
		this.$scope = $scope;
		this.ClientChooserWhitelist = ClientChooserWhitelist;
		this.searchQs = $location.search();

		this.setup();
	}

	/*
	 * Initalizes things on the page
	 */
	async setup() {
		await super.setup();

		this.needsHubChange = false;
		this.whitelistLoaded = false;
		this.mode = 'external';
		this.loadTaxonomy();
		this.resetPost();
		this.hasMultipleHubs = this.Identity.hasMultipleHubs();
		this.isInOwnWindow = true;
		this.setupPartner();
		this.setupPost();
		this.setupLabels();
	}

	/*
	 * Initializes external-partner-related things on the page
	 */
	async setupPartner() {
		this.partner = this.searchQs.partner || '';
		if (this.partner === 'msteams') {
			this.fetchHubWhitelistMsg = 'Getting list of integrated hubs';
			await this.setupWhitelist();
			this.needsHubChangeMsg = 'Please switch to a different hub. ' + 'This one is not enabled for the integration.';
			this.notAllowedMsg = 'Sorry, none of your hubs are enabled for this integration.';
			this.verifyCurrentHubIsWhitelisted();
		} else {
			this.whitelistLoaded = true;
		}
	}

	/*
	 * Initializes the hub whitelist; ie. those hubs that should be shown in the chooser
	 */
	async setupWhitelist() {
		this.isFetchingHubWhitelist = true;
		let updated = false;
		const resp = await this.Api.request('get', '/v3/msteams/integration/user');
		if (this.hasBadResponse(resp)) {
			this.setNotAllowed();
		} else {
			this.whitelistLoaded = true;
		}
		const whitelist = resp.data;
		this.ClientChooserWhitelist.whitelist = whitelist;
		this.isFetchingHubWhitelist = false;
	}

	/*
	 * Checks for bad api response
	 */
	hasBadResponse(resp) {
		return !resp.status || typeof resp.data == 'undefined' || resp.status < 200 || resp.status > 299;
	}

	/*
	 * Basically disables the entire form and presents a "not allowed" message
	 */
	setNotAllowed() {
		this.isNotAllowed = true;
	}

	/*
	 * Verifies whether or not the currently-choosen hub is in the hub whitelist
	 */
	verifyCurrentHubIsWhitelisted() {
		const curr = this.Identity.currentClient();
		let ret = true;
		if (!this.ClientChooserWhitelist.filter(curr)) {
			ret = false;
		}
		this.needsHubChange = !ret;
		return ret;
	}

	/*
	 * Initializes the post details or starts a web scrape
	 */
	setupPost() {
		if (this.searchQs.source_url) {
			this.originUrl = this.searchQs.source_url;
			this.fetchUrl();
		} else {
			this.setForm({
				rich_text_body: this.searchQs.content || '',
				source_name: this.partner,
				source_author: '',
				url: '',
				intel_text: '',
				classification: '',
			});
		}
	}

	/*
	 * Initializes the post form details
	 */
	setForm(data) {
		this.post.headline = data.title || '';
		this.post.rich_text_body = data.rich_text_body;
		this.post.source_date = this.handleSourceDate(data.source_date);
		this.post.source_author = data.source_author;
		this.post.source_name = data.source_name;
		this.post.source_link = data.url;
		this.post.intel_text = data.intel_text;
		this.post.tags = ensure.array(data.tag_with).map(name => {
			return { id: name, name, $search: name };
		});
		this.post.category = { id: data.category || 0 };
		this.classification = data.classification;
	}

	/*
	 * Gives us a source date
	 */
	handleSourceDate(dateStr) {
		if (dateStr) {
			const dateObj = moment(dateStr);
			if (dateObj.isValid()) {
				return dateObj;
			}
		}
		return moment();
	}

	/*
	 * Saves the post via api
	 */
	async save() {
		const validation = this.validatePost(this.post);

		if (validation.success) {
			try {
				const newPostId = await this.saveNew();
				if (this.partner === 'msteams') {
					await msTeamsApp.initialize();
					var res = {
						pid: newPostId,
						isPublished: this.isPublished ? '1' : '0',
						headline: this.post.headline,
					};
					await msTeamsDialog.submit(res);
				}
			} catch (e) {
				spinner.stopAll();
				this.handlErrors(e.errors);
			}
		} else {
			spinner.stopAll();
			this.showErrorsModal(validation.errors);
		}
	}

	/*
	 * Validates the form
	 */
	validate() {
		if (this.isNotAllowed || this.needsHubChange) {
			spinner.stopAll();
			this._alert('This action is not allowed');
			return false;
		}
		return super.validate();
	}

	/*
	 * Enhances the data to be saved before sending the call to the api
	 */
	augmentSaveData(data) {
		let medium = 'ext_source';
		switch (this.partner) {
			case 'msteams':
				medium = 'msteams';
				break;
		}
		data.submission_medium = medium;
		return data;
	}

	/*
	 * Client-change handler
	 */
	handleChangeClient() {
		const isGood = this.verifyCurrentHubIsWhitelisted();
		this.$scope.$apply();
		if (isGood) {
			this.loadNewTaxonomyAndTheme();
		}
	}

	/*
	 * Handler for when no hubs are available to choose from
	 */
	handleNoHubChoices() {
		this.setNotAllowed();
	}
}
